import type { QueryKey } from '@tanstack/react-query';
import {
  MessageStatus,
  type TCSearcherTimelineItemDocument,
} from 'src/api/generated';
import { getMessageClientMessageId } from 'src/modules/shared/messaging/utils/client-message-id.util';
import { shouldAddTimelineItemToQuery } from 'src/modules/shared/communications/utils/cache/query-matcher.util';
import type { OutboundMessageState } from '../../types/whatsapp/outbound-message.types';
import { toWhatsAppTimelineItem } from '../cache/whatsapp-update.util';
import { isMessageItem } from '../timeline/item-type-guards.util';
import { getOutboundMessageDestination } from './outbound-message.util';

const canonicalTimesByClientId = (items: TCSearcherTimelineItemDocument[]) => {
  const times = new Map<string, number>();
  for (const item of items) {
    if (!isMessageItem(item) || item.id.startsWith('temp-')) continue;
    const clientId = getMessageClientMessageId(item);
    if (clientId) times.set(clientId, Date.parse(item.createdAt));
  }
  return times;
};

const acknowledgedTimesByDestination = (
  submissions: (OutboundMessageState | undefined)[],
  canonicalTimes: Map<string, number>
) => {
  const times = new Map<string, number>();
  for (const entry of submissions) {
    if (!entry) continue;
    const time = canonicalTimes.get(entry.variables.optimisticMessage.id);
    if (time === undefined) continue;
    const destination = getOutboundMessageDestination(entry.variables);
    times.set(destination, Math.max(times.get(destination) ?? 0, time));
  }
  return times;
};

/**
 * Projects local sends over refreshed pages without modifying authoritative data.
 * Pending drafts remain after their canonical predecessor when its server time
 * advances beyond submission time, including socket delivery before HTTP.
 * @param queryKey - Timeline membership filters.
 * @param items - Cached timeline documents.
 * @param submissions - Account-owned mutations, including accepted predecessors.
 * @returns Timeline documents with independent pending/failed send state.
 */
export const projectPendingMessages = (
  queryKey: QueryKey,
  items: TCSearcherTimelineItemDocument[],
  submissions: (OutboundMessageState | undefined)[]
): TCSearcherTimelineItemDocument[] => {
  const local = submissions.filter((entry): entry is OutboundMessageState =>
    Boolean(entry && ['pending', 'error'].includes(entry.status))
  );
  const localIds = new Set(
    local.map((entry) => entry.variables.optimisticMessage.id)
  );
  const result = items.filter((item) => !localIds.has(item.id));
  const canonicalTimes = canonicalTimesByClientId(items);
  const acknowledgedTimes = acknowledgedTimesByDestination(
    submissions,
    canonicalTimes
  );
  const busy = new Set<string>();
  const displayTimes = new Map<string, number>();
  for (const { variables, status } of local) {
    const destination = getOutboundMessageDestination(variables);
    const phase = busy.has(destination) ? 'queued' : 'sending';
    if (status === 'pending') busy.add(destination);
    const displayTime = Math.max(
      Date.parse(variables.optimisticMessage.createdAt),
      status === 'pending' ? (acknowledgedTimes.get(destination) ?? 0) + 1 : 0,
      (displayTimes.get(destination) ?? 0) + 1
    );
    displayTimes.set(destination, displayTime);
    if (canonicalTimes.has(variables.optimisticMessage.id)) continue;
    const item = toWhatsAppTimelineItem(
      {
        ...variables.optimisticMessage,
        createdAt: new Date(displayTime).toISOString(),
        status:
          status === 'error' ? MessageStatus.FAILED : MessageStatus.PENDING,
        metadata: {
          ...variables.optimisticMessage.metadata,
          __communicationsSendState: phase,
        },
      },
      variables.params,
      variables.optimisticMessage
    );
    if (shouldAddTimelineItemToQuery(queryKey, item)) result.push(item);
  }
  return result;
};
